import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../services/api';

const Onboarding = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const school = user.school || {};

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    school_name: school.name || '',
    school_phone: school.phone || '',
    school_address: school.address || '',
    app_logo: null,
    academic_year: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1),
    currency: 'USD',
    student_id_prefix: 'STD'
  });

  const handleNext = () => {
    if (step === 1) {
      if (!formData.school_name || !formData.school_phone || !formData.school_address) {
        Swal.fire({ icon: 'error', title: 'Oops...', text: 'Please fill all required fields in Step 1.' });
        return;
      }
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.academic_year || !formData.currency || !formData.student_id_prefix) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Please fill all required fields in Step 2.' });
      return;
    }

    setSubmitting(true);
    const data = new FormData();
    data.append('school_name', formData.school_name);
    data.append('school_phone', formData.school_phone);
    data.append('school_address', formData.school_address);
    data.append('academic_year', formData.academic_year);
    data.append('currency', formData.currency);
    data.append('student_id_prefix', formData.student_id_prefix);
    
    if (formData.app_logo) {
      data.append('app_logo', formData.app_logo);
    }

    try {
      const response = await api.post('/settings/onboarding', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Update local storage user
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      Swal.fire({
        icon: 'success',
        title: 'Welcome Aboard!',
        text: 'Your school setup is complete.',
        timer: 2000,
        showConfirmButton: false
      }).then(() => {
        navigate('/'); // Go to dashboard
      });

    } catch (error) {
      console.error('Onboarding Error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Setup Failed',
        text: error.response?.data?.message || 'Something went wrong during setup. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100" style={{ background: '#f3f5f9' }}>
      <div className="card shadow-lg border-0" style={{ maxWidth: '800px', width: '100%', borderRadius: '20px', overflow: 'hidden' }}>
        <div className="row g-0">
          <div className="col-md-5 d-none d-md-block" style={{ background: 'linear-gradient(135deg, #4361ee 0%, #3046fe 100%)', color: 'white', padding: '40px' }}>
            <h2 className="fw-bold mb-4">Welcome to LMS!</h2>
            <p className="mb-4">Let's get your school set up in just a few quick steps.</p>
            
            <div className="d-flex align-items-center mb-3" style={{ opacity: step >= 1 ? 1 : 0.5 }}>
              <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '32px', height: '32px', background: step === 1 ? 'white' : 'rgba(255,255,255,0.2)', color: step === 1 ? '#4361ee' : 'white', fontWeight: 'bold' }}>1</div>
              <span>School Information</span>
            </div>
            
            <div className="d-flex align-items-center" style={{ opacity: step >= 2 ? 1 : 0.5 }}>
              <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '32px', height: '32px', background: step === 2 ? 'white' : 'rgba(255,255,255,0.2)', color: step === 2 ? '#4361ee' : 'white', fontWeight: 'bold' }}>2</div>
              <span>Basic Settings</span>
            </div>
          </div>
          
          <div className="col-md-7 p-5">
            <h4 className="fw-bold text-primary mb-4">{step === 1 ? 'School Information' : 'Basic Settings'}</h4>
            
            <form onSubmit={handleSubmit}>
              {step === 1 && (
                <div className="fade-in-up">
                  <div className="mb-3">
                    <label className="form-label fw-bold">School Name <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={formData.school_name} onChange={e => setFormData({...formData, school_name: e.target.value})} required placeholder="Enter school name" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Phone Number <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={formData.school_phone} onChange={e => setFormData({...formData, school_phone: e.target.value})} required placeholder="e.g. +1 234 567 8900" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Complete Address <span className="text-danger">*</span></label>
                    <textarea className="form-control" rows="2" value={formData.school_address} onChange={e => setFormData({...formData, school_address: e.target.value})} required placeholder="Enter complete physical address"></textarea>
                  </div>
                  <div className="mb-4">
                    <label className="form-label fw-bold">School Logo</label>
                    <input type="file" className="form-control" accept="image/*" onChange={e => setFormData({...formData, app_logo: e.target.files[0]})} />
                    <small className="text-muted">Recommended size: 200x200px. You can update this later.</small>
                  </div>
                  
                  <div className="d-flex justify-content-end mt-4">
                    <button type="button" className="btn btn-primary px-4" onClick={handleNext}>Next Step <i className="bi bi-arrow-right ms-2"></i></button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="fade-in-up">
                  <div className="mb-3">
                    <label className="form-label fw-bold">Academic Year <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={formData.academic_year} onChange={e => setFormData({...formData, academic_year: e.target.value})} required placeholder="e.g. 2026-2027" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-bold">Currency <span className="text-danger">*</span></label>
                    <select className="form-select" value={formData.currency} onChange={e => setFormData({...formData, currency: e.target.value})} required>
                      <option value="PKR">Pakistani Rupee (PKR)</option>
                      <option value="USD">US Dollar (USD)</option>
                      <option value="EUR">Euro (EUR)</option>
                      <option value="GBP">British Pound (GBP)</option>
                      <option value="INR">Indian Rupee (INR)</option>
                      <option value="AED">UAE Dirham (AED)</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="form-label fw-bold">Student ID Prefix <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" value={formData.student_id_prefix} onChange={e => setFormData({...formData, student_id_prefix: e.target.value})} required placeholder="e.g. STD" maxLength="10" />
                    <small className="text-muted">Example: If prefix is STD, IDs will look like STD-2026-001</small>
                  </div>

                  <div className="d-flex justify-content-between mt-5">
                    <button type="button" className="btn btn-light px-4" onClick={handleBack}><i className="bi bi-arrow-left me-2"></i> Back</button>
                    <button type="submit" className="btn btn-primary px-4" disabled={submitting}>
                      {submitting ? 'Setting up...' : 'Complete Setup'} <i className="bi bi-check-circle ms-2"></i>
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
